/* MUV — search modal results, collection first.
   If the query names a category (its collection title or one of the words in
   the collection metafield "Palabras de búsqueda"), we show that collection's
   products in the order LUV set, with a link to the collection. Extra words
   narrow it down ("silla verde", "mesa teca") and a second category word
   intersects ("mesa exterior"). Anything else (model names, colours...) falls
   back to Shopify's product search via /search/suggest.json.
   Collections and UI strings come from #muv-search-data (search-modal.liquid). */
(function () {
  const INPUT_SELECTOR = '#Search-In-Modal-1';
  const CONTAINER_SELECTOR = '#muv-search-cards';
  const DATA_SELECTOR = '#muv-search-data';
  const LIMIT = 8;
  const DEBOUNCE_MS = 250;
  const STOPWORDS = new Set(['de', 'del', 'la', 'las', 'el', 'los', 'para', 'con', 'y', 'en', 'a', 'un', 'una', 'of', 'the', 'for', 'with', 'and', 'in', 'an']);

  // Locale root ("/es/" on the Spanish site). Read from the URL as well as from
  // Shopify.routes.root: since 28/09/2026 Shopify renders "/" there for new
  // visitors on /es/ pages (platform bug), which would send them to English.
  const ROOT = (() => {
    const shopifyRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (shopifyRoot !== '/') return shopifyRoot;
    const match = location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i);
    const lang = (document.documentElement.lang || '').toLowerCase();
    if (match && lang && lang.slice(0, 2) === match[1].slice(0, 2).toLowerCase()) return `/${match[1]}/`;
    return '/';
  })();

  // /search/suggest.json and products.json return price as a STRING in euros
  // (e.g. "568.70"), NOT in cents. The store shows prices as "569 €" (no decimals,
  // symbol after), so we round and group thousands with "." manually — the
  // JS engine in this Shopify storefront context does not always load es-ES
  // locale data, so toLocaleString('es-ES') silently falls back to no grouping.
  const moneyFormat = (price) => {
    if (price === null || price === undefined || price === '') return '';
    const n = parseFloat(price);
    if (isNaN(n)) return '';
    const rounded = Math.round(n).toString();
    const grouped = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return grouped + ' €';
  };

  const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const fill = (template, values) => String(template || '').replace(/\{(\w+)\}/g, (m, key) => (key in values ? values[key] : m));

  // Lowercase, no accents, only letters/digits.
  const normalize = (str) => String(str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const tokenize = (str) => normalize(str).split(' ').filter((t) => t && !STOPWORDS.has(t));

  // Singular/plural tolerant: taburete ~ taburetes, aparador ~ aparadores, table ~ tables.
  const sameWord = (a, b) => a === b || a === b + 's' || a === b + 'es' || b === a + 's' || b === a + 'es';

  // Filters also ignore Spanish gender: roja ~ rojo, blancas ~ blanco.
  const sameFilterWord = (a, b) => {
    if (sameWord(a, b)) return true;
    const stem = (w) => (w.length >= 4 ? w.replace(/(a|o)s?$/, '') : w);
    return stem(a).length >= 3 && stem(a) === stem(b);
  };

  const stripHtml = (html) => String(html || '').replace(/<[^>]*>/g, ' ');

  const collectionUrl = (handle) => `${ROOT}collections/${handle}`;
  const productUrl = (handle) => `${ROOT}products/${handle}`;

  let DATA = null;
  const readData = () => {
    if (DATA) return DATA;
    const el = document.querySelector(DATA_SELECTOR);
    let parsed = null;
    try { parsed = el ? JSON.parse(el.textContent) : null; } catch (e) { parsed = null; }
    const collections = ((parsed && parsed.collections) || []).map((c) => {
      const phrases = [c.title].concat(c.keywords || [])
        .map((p) => tokenize(p))
        .filter((p) => p.length);
      return Object.assign({}, c, { phrases });
    });
    // Parent lookup, used when a collection is empty (e.g. Camas → Dormitorio).
    collections.forEach((c) => {
      c.parents = collections.filter((p) => (p.children || []).includes(c.handle));
    });
    // The t filter HTML-escapes its output ("don&#39;t"); decode once here,
    // strings are escaped again when rendered.
    const decode = (str) => {
      const el = document.createElement('textarea');
      el.innerHTML = String(str || '');
      return el.value;
    };
    const i18n = {};
    Object.keys((parsed && parsed.i18n) || {}).forEach((key) => { i18n[key] = decode(parsed.i18n[key]); });
    const vendorWords = new Set(((parsed && parsed.vendors) || []).flatMap((v) => tokenize(v)).filter((w) => w.length > 2));
    DATA = { i18n, collections, vendorWords };
    return DATA;
  };

  // Where a phrase appears in the query tokens (contiguous). The last phrase
  // word may be a prefix of the last query word while the user is typing.
  function findPhrase(queryTokens, phrase, allowedIdx) {
    const n = phrase.length;
    for (let start = 0; start + n <= queryTokens.length; start++) {
      let prefix = false;
      let ok = true;
      for (let k = 0; k < n; k++) {
        const qi = start + k;
        if (allowedIdx && !allowedIdx.has(qi)) { ok = false; break; }
        const q = queryTokens[qi];
        const p = phrase[k];
        if (sameWord(q, p)) continue;
        const isLastQueryToken = qi === queryTokens.length - 1;
        if (k === n - 1 && isLastQueryToken && q.length >= 3 && p.startsWith(q)) { prefix = true; continue; }
        ok = false;
        break;
      }
      if (ok) return { start, len: n, prefix };
    }
    return null;
  }

  // Ranking: more query words covered; while typing, the shortest completion
  // ("mes" → Mesas, not Mesitas); then the word typed first ("mesa exterior"
  // → Mesas, narrowed to Exterior); then a collection with products, the
  // most specific one.
  const isBetter = (a, b) => {
    if (!b) return true;
    if (a.score !== b.score) return a.score > b.score;
    if (a.hit.prefix && b.hit.prefix && a.phraseLen !== b.phraseLen) return a.phraseLen < b.phraseLen;
    if (a.hit.start !== b.hit.start) return a.hit.start < b.hit.start;
    if ((a.collection.count > 0) !== (b.collection.count > 0)) return a.collection.count > 0;
    return a.collection.count < b.collection.count;
  };

  // Best collection for the query tokens (optionally restricted to some token positions).
  function bestCollection(queryTokens, allowedIdx, exclude) {
    const { collections } = readData();
    let best = null;
    collections.forEach((c) => {
      if (exclude && exclude.has(c.handle)) return;
      c.phrases.forEach((phrase) => {
        const hit = findPhrase(queryTokens, phrase, allowedIdx);
        if (!hit) return;
        const cand = { collection: c, hit, score: hit.len * 2 - (hit.prefix ? 1 : 0), phraseLen: phrase.join(' ').length };
        if (isBetter(cand, best)) best = cand;
      });
    });
    return best;
  }

  // Query → { primary, secondary, terms } where terms are the leftover words.
  function analyse(term) {
    const tokens = tokenize(term);
    if (!tokens.length) return null;
    const primary = bestCollection(tokens);
    if (!primary) return null;
    const used = new Set();
    for (let i = primary.hit.start; i < primary.hit.start + primary.hit.len; i++) used.add(i);
    const rest = new Set(tokens.map((t, i) => i).filter((i) => !used.has(i)));
    let secondary = null;
    if (rest.size) {
      const cand = bestCollection(tokens, rest, new Set([primary.collection.handle]));
      if (cand && !cand.hit.prefix) {
        secondary = cand;
        for (let i = cand.hit.start; i < cand.hit.start + cand.hit.len; i++) rest.delete(i);
      }
    }
    // A last word shorter than 3 letters is still being typed: not a filter yet.
    const last = tokens.length - 1;
    if (rest.has(last) && tokens[last].length < 3 && !/\d/.test(tokens[last])) rest.delete(last);
    const terms = [...rest].sort((a, b) => a - b).map((i) => tokens[i]).filter((t) => t.length >= 2 || /\d/.test(t));
    return {
      tokens,
      primary: primary.collection,
      primaryIsPrefix: primary.hit.prefix,
      secondary: secondary && secondary.collection,
      terms,
      lastIsTerm: rest.has(last),
    };
  }

  // A supplier name ("vical", "micadoni"): no results anywhere, so the store
  // does not reveal which brands it sells.
  const isVendorQuery = (term) => tokenize(term).some((t) => readData().vendorWords.has(t));

  const productsCache = new Map();
  function fetchCollectionProducts(handle) {
    if (!productsCache.has(handle)) {
      const promise = fetch(`${collectionUrl(handle)}/products.json?limit=250`, { headers: { Accept: 'application/json' } })
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then((data) => (data && data.products) || [])
        .catch(() => { productsCache.delete(handle); return null; });
      productsCache.set(handle, promise);
    }
    return productsCache.get(handle);
  }

  // Colour siblings share the model name (Lech beige / Lech cream): one card per model.
  const dedupeByTitle = (products) => {
    const seen = new Set();
    return products.filter((p) => {
      const key = normalize(p.title);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  function matchesTerms(product, terms, lastIsPrefix) {
    // Supplier names must not be searchable (MUV does not show who makes what):
    // skip tags that repeat the vendor ("VICAL", "Micadoni"...).
    const vendor = normalize(product.vendor);
    const tags = (product.tags || []).filter((tag) => normalize(tag) !== vendor);
    const hay = tokenize([
      product.title,
      product.product_type,
      tags.join(' '),
      stripHtml(product.body_html),
      (product.variants || []).map((v) => v.title).join(' '),
      (product.options || []).map((o) => (o.values || []).join(' ')).join(' '),
    ].join(' '));
    return terms.every((t, i) => hay.some((h) => sameFilterWord(h, t) || (lastIsPrefix && i === terms.length - 1 && t.length >= 3 && h.startsWith(t))));
  }

  // Lowest price, among available variants when there are any (same as the
  // product cards and /search/suggest.json).
  const minPrice = (product) => {
    const variants = product.variants || [];
    const pool = variants.filter((v) => v.available).length ? variants.filter((v) => v.available) : variants;
    const prices = pool.map((v) => parseFloat(v.price)).filter((n) => !isNaN(n));
    return prices.length ? Math.min(...prices) : '';
  };

  const imageSrc = (src) => (src ? src + (src.includes('?') ? '&' : '?') + 'width=500' : '');

  function cardHtml({ title, url, image, price }) {
    const img = image
      ? `<img class="muv-search-card__image" src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="lazy">`
      : '<div class="muv-search-card__image muv-search-card__image--placeholder"></div>';
    const priceHtml = (price !== undefined && price !== null && price !== '')
      ? `<span class="muv-search-card__price">${moneyFormat(price)}</span>`
      : '';
    return `
      <a class="muv-search-card" href="${escapeHtml(url)}" aria-label="${escapeHtml(title)}">
        <div class="muv-search-card__media">${img}</div>
        <div class="muv-search-card__info">
          <span class="muv-search-card__title">${escapeHtml(title)}</span>
          ${priceHtml}
        </div>
      </a>
    `;
  }

  function renderCollection(container, { collection, products, total, note }) {
    const { i18n } = readData();
    const cards = products.slice(0, LIMIT).map((p) => cardHtml({
      title: p.title,
      url: productUrl(p.handle),
      image: imageSrc(p.images && p.images[0] && p.images[0].src),
      price: minPrice(p),
    })).join('');
    container.innerHTML = `
      <div class="muv-search-cards__head">
        <a class="muv-search-cards__heading muv-search-cards__heading--link" href="${escapeHtml(collectionUrl(collection.handle))}">${escapeHtml(collection.title)}</a>
        <a class="muv-search-cards__see-all" href="${escapeHtml(collectionUrl(collection.handle))}">${escapeHtml(fill(i18n.see_all, { count: total }))}</a>
      </div>
      ${note ? `<div class="muv-search-cards__note">${escapeHtml(note)}</div>` : ''}
      <div class="muv-search-cards__grid">${cards}</div>
    `;
  }

  // Smallest non-empty collection that lists this one as a subcollection
  // (Camas → Dormitorio, Lámparas de pie → Iluminación).
  const parentWithProducts = (collection) => (collection.parents || [])
    .filter((p) => p.count > 0 && p.handle !== 'all-products')
    .sort((a, b) => a.count - b.count)[0];

  // Empty category ("cama"): say so, and show products from the collection it
  // belongs to (Camas → Dormitorio) so the visitor has somewhere to go.
  function renderEmptyCollection(container, collection, current) {
    const { i18n } = readData();
    const parent = parentWithProducts(collection);
    const message = `
      <div class="muv-search-cards__heading">${escapeHtml(collection.title)}</div>
      <div class="muv-search-cards__empty">${escapeHtml(fill(i18n.empty_collection, { collection: collection.title.toLowerCase() }))}</div>
    `;
    const link = parent
      ? `<a class="muv-search-cards__see-all" href="${escapeHtml(collectionUrl(parent.handle))}">${escapeHtml(fill(i18n.see_collection, { collection: parent.title }))}</a>`
      : '';
    container.innerHTML = message + link;
    if (!parent) return;
    fetchCollectionProducts(parent.handle).then((products) => {
      if ((current && !current()) || !products || !products.length) return;
      const cards = dedupeByTitle(products).slice(0, LIMIT).map((p) => cardHtml({
        title: p.title,
        url: productUrl(p.handle),
        image: imageSrc(p.images && p.images[0] && p.images[0].src),
        price: minPrice(p),
      })).join('');
      container.innerHTML = `
        ${message}
        <div class="muv-search-cards__head">
          <div class="muv-search-cards__heading">${escapeHtml(i18n.maybe)}</div>
          ${link}
        </div>
        <div class="muv-search-cards__grid">${cards}</div>
      `;
    });
  }

  // Plain product results. `heading` defaults to "Productos"; when these are
  // only Shopify's loose suggestions we label them "Quizá te interese".
  function renderProducts(container, products, heading) {
    const { i18n } = readData();
    if (!products || !products.length) {
      container.innerHTML = `
        <div class="muv-search-cards__heading">${escapeHtml(i18n.products)}</div>
        <div class="muv-search-cards__empty">${escapeHtml(i18n.no_results)}</div>
      `;
      return;
    }
    // Catalogue products (products.json) have no `url`; Shopify's suggestions do.
    const cards = products.map((p) => cardHtml(!p.url
      ? {
        title: p.title,
        url: productUrl(p.handle),
        image: imageSrc(p.images && p.images[0] && p.images[0].src),
        price: minPrice(p),
      }
      : {
        title: p.title,
        url: p.url,
        // Predictive search returns featured_image either as a string URL or
        // as an object with .url; sometimes only `image.url` is present.
        image: (typeof p.featured_image === 'string' && p.featured_image)
          || (p.featured_image && p.featured_image.url)
          || (p.image && p.image.url)
          || '',
        price: p.price,
      })).join('');
    container.innerHTML = `
      <div class="muv-search-cards__heading">${escapeHtml(heading || i18n.products)}</div>
      <div class="muv-search-cards__grid">${cards}</div>
    `;
  }

  function productSearch(term) {
    const url = `${ROOT}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=${LIMIT}&resources[options][fields]=title,product_type,variants.title`;
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => (data && data.resources && data.resources.results && data.resources.results.products) || []);
  }

  // No category in the query (a model name, a colour, a material, a hidden
  // category like "tumbona"): look for the words in the whole catalogue first
  // (name, type, tags, description). Only if nothing matches, fall back to
  // Shopify's loose suggestions, labelled as such.
  function catalogueSearch(container, term, current) {
    const tokens = tokenize(term).filter((t) => t.length >= 2 || /\d/.test(t));
    const all = readData().collections.find((c) => c.handle === 'all-products');
    const fallback = () => productSearch(term)
      .then((found) => { if (current()) renderProducts(container, found, found.length ? readData().i18n.maybe : ''); })
      .catch(() => { if (current()) container.innerHTML = ''; });
    if (!tokens.length || !all) { fallback(); return; }
    fetchCollectionProducts(all.handle).then((products) => {
      if (!current()) return;
      const scored = (products || [])
        .filter((p) => matchesTerms(p, tokens, true))
        .map((p, i) => {
          const inTitle = tokens.every((t) => tokenize(p.title).some((h) => sameFilterWord(h, t) || h.startsWith(t)));
          const inType = tokens.some((t) => tokenize(p.product_type).some((h) => sameFilterWord(h, t)));
          return { p, i, score: (inTitle ? 2 : 0) + (inType ? 1 : 0) };
        })
        .sort((a, b) => b.score - a.score || a.i - b.i)
        .map((x) => x.p);
      const found = dedupeByTitle(scored).slice(0, LIMIT);
      // A single product ("Tarn"): Enter opens it.
      if (found.length === 1) lastShown = { term: term.trim(), url: productUrl(found[0].handle) };
      if (found.length) renderProducts(container, found);
      else fallback();
    });
  }

  // What the modal shows for a query that names a category, decided once so
  // the results, the "Ver todos" link and Enter all agree.
  let lastShown = null;

  let requestId = 0;
  function performSearch(container, term) {
    const id = ++requestId;
    const current = () => id === requestId;
    lastShown = null;
    if (!term || term.trim().length < 1) {
      container.innerHTML = '';
      return;
    }
    if (isVendorQuery(term)) {
      renderProducts(container, []);
      return;
    }
    const match = analyse(term);
    if (!match) {
      catalogueSearch(container, term, current);
      return;
    }
    const { i18n } = readData();
    let { primary, secondary } = match;
    const { terms, lastIsTerm } = match;
    let approximate = false;
    if (secondary && secondary.count === 0 && primary.count > 0) {
      // "sofá cama" while there are no beds: show sofas, say it is approximate.
      secondary = null;
      approximate = true;
    }
    // Empty first category but the second one has products: lead with it.
    if (primary.count === 0 && secondary && secondary.count > 0) {
      primary = secondary;
      secondary = null;
      approximate = true;
    }
    if (primary.count === 0) {
      if (match.primaryIsPrefix) {
        // Half-typed word that happens to start like an empty collection.
        catalogueSearch(container, term, current);
      } else {
        const parent = parentWithProducts(primary);
        if (parent) lastShown = { term: term.trim(), handle: parent.handle };
        renderEmptyCollection(container, primary, current);
      }
      return;
    }
    Promise.all([
      fetchCollectionProducts(primary.handle),
      secondary ? fetchCollectionProducts(secondary.handle) : Promise.resolve(null),
    ]).then(([primaryProducts, secondaryProducts]) => {
      if (!current()) return;
      if (!primaryProducts || !primaryProducts.length) {
        // Network error or nothing published after all.
        catalogueSearch(container, term, current);
        return;
      }
      let products = primaryProducts;
      if (secondaryProducts && secondaryProducts.length) {
        const inSecondary = new Set(secondaryProducts.map((p) => p.handle));
        const both = products.filter((p) => inSecondary.has(p.handle));
        // "mesa exterior" → outdoor tables. Nothing in common ("lámpara
        // exterior"): keep the first category and say it is approximate.
        if (both.length) products = both;
        else approximate = true;
      }
      if (terms.length) {
        const filtered = products.filter((p) => matchesTerms(p, terms, lastIsTerm));
        if (filtered.length) products = filtered;
        else approximate = true;
      }
      const note = approximate ? fill(i18n.no_match, { terms: term.trim() }) : '';
      lastShown = { term: term.trim(), handle: primary.handle };
      // One card per model, after filtering, so the colour that matched is kept.
      renderCollection(container, { collection: primary, products: dedupeByTitle(products), total: primary.count, note });
    }).catch(() => { if (current()) container.innerHTML = ''; });
  }

  // Enter in the search box: go to the collection the modal is showing (or,
  // before it has rendered, the one the query names); otherwise keep the full
  // search page in the visitor's language.
  function onSubmit(event) {
    const form = event.target;
    if (!form || !form.matches || !form.matches('form.search')) return;
    const input = form.querySelector('input[name="q"]');
    const term = input ? input.value.trim() : '';
    if (!term) return;
    if (isVendorQuery(term)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const container = document.querySelector(CONTAINER_SELECTOR);
      if (container) renderProducts(container, []);
      return;
    }
    if (lastShown && lastShown.term === term && lastShown.url) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.href = lastShown.url;
      return;
    }
    let handle = lastShown && lastShown.term === term ? lastShown.handle : null;
    if (!handle) {
      const match = analyse(term);
      if (match && !match.primaryIsPrefix) {
        const target = match.primary.count > 0 ? match.primary
          : (match.secondary && match.secondary.count > 0 ? match.secondary : parentWithProducts(match.primary));
        if (target) handle = target.handle;
      }
    }
    if (handle) {
      event.preventDefault();
      event.stopImmediatePropagation();
      // These searches skip the results page, so Shopify's search_submitted
      // event does not fire; leave a custom event for pixels that want it.
      try {
        if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
          window.Shopify.analytics.publish('muv_search_to_collection', { query: term, collection: handle });
        }
      } catch (e) { /* analytics must never block navigation */ }
      window.location.href = collectionUrl(handle);
      return;
    }
    form.setAttribute('action', `${ROOT}search`);
  }

  // Search results page reached directly with a category word (e.g. from
  // Google's site search box): take the visitor to the collection instead.
  function redirectSearchPage() {
    const searchPath = new RegExp(`^${ROOT.replace(/\//g, '\\/')}search\\/?$`);
    if (!searchPath.test(location.pathname)) return;
    const params = new URLSearchParams(location.search);
    const term = (params.get('q') || '').trim();
    if (!term || params.has('page') || params.has('type') || params.has('sort_by')) return;
    if ([...params.keys()].some((key) => key.startsWith('filter.'))) return;
    const match = analyse(term);
    if (match && match.primary.count > 0 && !match.primaryIsPrefix && !match.terms.length && !match.secondary) {
      window.location.replace(collectionUrl(match.primary.handle));
    }
  }

  function bind() {
    const input = document.querySelector(INPUT_SELECTOR);
    const container = document.querySelector(CONTAINER_SELECTOR);
    if (!input || !container) return;
    if (input.dataset.muvSearchBound === 'true') return;
    input.dataset.muvSearchBound = 'true';

    let debounceId = null;
    input.addEventListener('input', (event) => {
      const value = event.target.value;
      clearTimeout(debounceId);
      debounceId = setTimeout(() => performSearch(container, value), DEBOUNCE_MS);
    });
    // Reset when the user clears via the X icon
    const clearIcon = document.querySelector('.search__input--icon-clear');
    if (clearIcon) {
      clearIcon.addEventListener('click', () => {
        clearTimeout(debounceId);
        requestId++;
        container.innerHTML = '';
      });
    }
  }

  document.addEventListener('submit', onSubmit, true);

  function init() {
    bind();
    redirectSearchPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', bind);
})();
